import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseKickoffAndPollOptions {
	intervalMs?: number;
	maxWaitMs?: number;
	// If true, do not auto-stop on unmount. Useful when sync state is tracked in Redux.
	persistAcrossUnmount?: boolean;
	// Notify external stores (e.g., Redux) about running state transitions
	onStatusChange?: (isRunning: boolean) => void;
}

export interface ProcessStateLike {
	processing_status_stages?: boolean;
}

interface UseKickoffAndPollArgs<T extends ProcessStateLike = ProcessStateLike> {
	kickoff: () => Promise<T | undefined>;
	check: () => Promise<T | undefined>;
	onKickoff?: () => Promise<void> | void;
	onComplete?: () => Promise<void> | void;
	options?: UseKickoffAndPollOptions;
}

export function useKickoffAndPoll<T extends ProcessStateLike = ProcessStateLike>({
	kickoff,
	check,
	onKickoff,
	onComplete,
	options
}: UseKickoffAndPollArgs<T>) {
	const [isRunning, setIsRunning] = useState(false);
	const timerRef = useRef<number | ReturnType<typeof setTimeout> | null>(null);
	const startTimeRef = useRef<number>(0);
	const stoppedRef = useRef<boolean>(false);

	const clearTimer = () => {
		if (timerRef.current) {
			clearTimeout(timerRef.current as any);
			timerRef.current = null;
		}
	};

	const stop = useCallback(() => {
		stoppedRef.current = true;
		clearTimer();
		setIsRunning(false);
	}, []);

	useEffect(() => {
		return () => {
			// Cleanup on unmount unless persistence is requested
			if (!options?.persistAcrossUnmount) {
				stop();
			}
		};
	}, [stop, options?.persistAcrossUnmount]);

	const start = useCallback(async () => {
		setIsRunning(true);
		if (options?.onStatusChange) 
			options.onStatusChange(true);
		stoppedRef.current = false;
		startTimeRef.current = Date.now();

		const intervalMs = options?.intervalMs ?? 1000;
		const maxWaitMs = options?.maxWaitMs ?? 5 * 60 * 1000;

		const kickoffResult = await kickoff();
		if (stoppedRef.current) {
			stop();
			return;
		}
		if (onKickoff) await onKickoff();
		if (stoppedRef.current) {
			stop();
			return;
		}

		const tick = async () => {
			if (stoppedRef.current) {
				stop();
				return;
			}
			if (Date.now() - startTimeRef.current > maxWaitMs) {
				stop();
				return;
			}
			const checkResult = await check();
			if (stoppedRef.current) {
				stop();
				return;
			}
			if (checkResult?.processing_status_stages === false) {
				if (onComplete) await onComplete();
				if (options?.onStatusChange) 
					options.onStatusChange(false);
				stop();
				return;
			}
			clearTimer();
			timerRef.current = setTimeout(tick, intervalMs) as any;
		};

		// Only poll if kickoff indicates backend still processing
		if (kickoffResult?.processing_status_stages === true) {
			timerRef.current = setTimeout(tick, intervalMs) as any;
		} else {
			if (options?.onStatusChange) 
				options.onStatusChange(false);
			// If kickoff already indicates completion, invoke onComplete immediately
			if (onComplete) await onComplete();
			stop();
		}
	}, [check, kickoff, onComplete, onKickoff, options?.intervalMs, options?.maxWaitMs, options?.onStatusChange, stop]);

	return { start, stop, isRunning };
}